import { useState, useEffect } from 'react'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import {
  Alert,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { AlertCircle, CheckCircle, CreditCard, X } from 'lucide-react'
import { paymentApi } from '../api/payment'

let cachedStripe: Stripe | null = null

async function getStripeInstance(): Promise<Stripe | null> {
  if (!cachedStripe) {
    const res = await paymentApi.getStripeConfig()
    if (res.data.success && res.data.publishable_key) {
      cachedStripe = await loadStripe(res.data.publishable_key)
    } else {
      throw new Error('Failed to get Stripe config')
    }
  }
  return cachedStripe
}

// Inner form rendered inside <Elements>
function PaymentForm({
  paymentIntentId,
  amount,
  planCode,
  onSuccess,
  onCancel,
}: {
  paymentIntentId: string
  amount: number
  planCode: string
  onSuccess: () => void
  onCancel: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setProcessing(true)
    setError(null)
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      })
      if (stripeError) {
        setError(stripeError.message ?? 'Payment failed')
        return
      }
      if (paymentIntent?.status === 'succeeded') {
        const res = await paymentApi.confirmPayment({ payment_intent_id: paymentIntentId, plan_code: planCode })
        // Save the fresh JWT so the new subscription_status / plan_code takes effect immediately
        if (res.data.new_token) {
          localStorage.setItem('sat_token', res.data.new_token)
        }
        notifications.show({
          title: 'Payment successful',
          message: 'Your subscription is now active!',
          color: 'green',
          icon: <CheckCircle size={16} />,
        })
        onSuccess()
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Payment failed')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Text size="lg" fw={600} ta="center">Complete Your Payment</Text>
        <Text size="sm" c="dimmed" ta="center">Amount: ${(amount / 100).toFixed(2)} USD</Text>

        <Card withBorder p="md" radius="md">
          <PaymentElement options={{ layout: 'tabs' }} />
        </Card>

        {error && (
          <Alert icon={<AlertCircle size={16} />} title="Payment Error" color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="space-between" mt="xs">
          <Button variant="subtle" onClick={onCancel} disabled={processing}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={processing}
            disabled={!stripe || !elements}
            leftSection={<CreditCard size={16} />}
          >
            {processing ? 'Processing…' : 'Pay Now'}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

export interface PaymentModalProps {
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
  planName: string
  planCode: string
  amount: number        // in cents
  description?: string
  userId: number
}

export function PaymentModal({
  opened,
  onClose,
  onSuccess,
  planName,
  planCode,
  amount,
  description,
}: PaymentModalProps) {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [clientSecret, setClientSecret] = useState<string>('')
  const [paymentIntentId, setPaymentIntentId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset when modal closes
  useEffect(() => {
    if (!opened) {
      setClientSecret('')
      setPaymentIntentId('')
      setError(null)
    }
  }, [opened])

  // Load Stripe + create PaymentIntent when modal opens
  useEffect(() => {
    if (!opened) return
    setLoading(true)
    Promise.all([
      getStripeInstance(),
      paymentApi.createPaymentIntent({ amount, description: description ?? planName, plan_code: planCode }),
    ])
      .then(([sp, res]) => {
        setStripe(sp)
        setClientSecret(res.data.client_secret)
        setPaymentIntentId(res.data.payment_intent_id)
      })
      .catch((err) => {
        setError(err?.response?.data?.detail ?? 'Failed to initialize payment')
        notifications.show({ title: 'Error', message: 'Failed to initialize payment', color: 'red' })
      })
      .finally(() => setLoading(false))
  }, [opened]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSuccess() {
    onSuccess?.()
    onClose()
  }

  const appearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#0570de',
      colorBackground: '#ffffff',
      colorText: '#30313d',
      colorDanger: '#df1b41',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      borderRadius: '4px',
    },
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Subscribe to ${planName}`}
      size="md"
      centered
      closeButtonProps={{ icon: <X size={18} /> }}
      overlayProps={{ backgroundOpacity: 0.8, blur: 3 }}
      transitionProps={{ transition: 'fade', duration: 200 }}
    >
      <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />

      {error && (
        <Alert icon={<AlertCircle size={16} />} title="Error" color="red" variant="light" mb="md">
          {error}
        </Alert>
      )}

      {!loading && !error && clientSecret && stripe && (
        <Elements stripe={stripe} options={{ clientSecret, appearance }}>
          <PaymentForm
            paymentIntentId={paymentIntentId}
            amount={amount}
            planCode={planCode}
            onSuccess={handleSuccess}
            onCancel={onClose}
          />
        </Elements>
      )}
    </Modal>
  )
}
